import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
	getMomentTz,
	dxtToLocalServerTime,
	useContextMenuMESF,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { Box, Button, Paper, Switch, Tooltip, Typography } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { Moment } from "moment-timezone";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	isUsableDate,
	urlParam,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { formatDuration } from "@/shared/utils/formatDuration";
import { DelaysManagerFilters } from "./components/filters";
import { DelaysManagerTable } from "./components/table/DelaysManagerTable";
import { useDelays } from "./hooks";
import { StartFilter, EndFilter } from "../shared/components/StartEndFilter";

const moment = getMomentTz();

const REFRESH_INTERVAL_MS = 10_000;

const URL_SCHEMA = {
	mode: urlParam.oneOf(["period", "shift"] as const, "period"),
	start: urlParam.date(() => moment().subtract(1, "days").startOf("day"), {
		moment,
	}),
	end: urlParam.date(() => moment().endOf("day"), { moment }),
	assetId: urlParam.int(),
	delayCategoryId: urlParam.int(),
	delayCodeId: urlParam.int(),
	crewId: urlParam.string(),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	search: urlParam.string(),
};

export const DelaysManagerPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
	const gridApiRef = useRef<GridApi | null>(null);
	const { show: isRowMenuOpen } = useContextMenuMESF();
	// Held while the user works on a stop so a reload never lands mid-edit.
	const isRefreshHeld = isStopDialogOpen || isRowMenuOpen;

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	// Every filter, the navigation mode and the period live in the query string. The dialog
	// keys (action, delayId) and the ones the justify dialog is handed (passingMode,
	// branchCodeId) are not in the schema, so the hook leaves them untouched.
	const [
		{
			mode: navMode,
			start: filterStartDate,
			end: filterEndDate,
			assetId,
			delayCategoryId,
			delayCodeId,
			crewId: crew,
			productTypeId,
			productSizeId,
			search,
		},
		setFilters,
		{ resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [quickFilter, setQuickFilter] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);

	const delayId = params.get("delayId");
	const action = params.get("action") as "edit" | "justify" | "split" | null;

	// Opening or closing a stop dialog. It keeps every other key, so the filters the
	// operator set survive a justify, and it still pushes: Back closing the dialog is what
	// the screen does today.
	const updateUrl = (newAction?: string | null, newDelayId?: string | null) => {
		const shiftId = get(shiftInfo, "CurrentShiftId", null);
		const next = new URLSearchParams(window.location.search);
		const put = (key: string, value?: string | null) => {
			if (value === undefined) return;
			if (value === null || value === "") next.delete(key);
			else next.set(key, value);
		};
		put("action", newAction);
		put("delayId", newDelayId);
		navigate(`${routeDelaysManager}/${shiftId}?${next.toString()}`);
	};

	// Single hook handles both shift mode (ShiftId) and period mode (StartDate/EndDate)
	const {
		data: delaysData,
		refetch,
		isLoading,
		error: queryError,
	} = useDelays({
		shiftId: navMode === "shift" ? (shiftInfo?.CurrentShiftId ?? null) : null,
		startDate:
			navMode === "period" ? filterStartDate.format("YYYY-MM-DD") : null,
		endDate: navMode === "period" ? filterEndDate.format("YYYY-MM-DD") : null,
		delayAreaAssetId: assetId,
		crew: crew,
		delayCategoryId: delayCategoryId,
		delayCodeId: delayCodeId,
		productTypeId: productTypeId,
		productSizeId: productSizeId,
		enabled:
			navMode === "period" ||
			(navMode === "shift" && !!shiftInfo?.CurrentShiftId),
		refetchInterval: isRefreshHeld ? false : REFRESH_INTERVAL_MS,
	});

	// Stops saved from any browser arrive at once through the broadcast; stops
	// detected by delay tracking never pass through the API, so the poll above
	// stays for those.
	// Not meta.realtime on the query: a refresh is held while a stop dialog or a
	// row menu is open, and invalidation would refetch regardless.
	useMesfRealtime({
		topics: ["DEL.GetDelays"],
		onMessage: () => {
			if (!isRefreshHeld) {
				refetch();
			}
		},
	});

	const displayRows = delaysData?.rows || [];
	const displaySummary = delaysData?.summary || {
		count: 0,
		durationSeconds: 0,
	};

	useEffect(() => {
		if (queryError) {
			setError(queryError.message);
		}
	}, [queryError]);

	// The shift the navigator is on belongs in the path. It replaces the entry: Back has to
	// leave the screen, not walk back through every shift looked at.
	useEffect(() => {
		if (navMode !== "shift" || !shiftInfo) return;
		const shiftId = get(shiftInfo, "CurrentShiftId", null);
		if (!shiftId || String(shiftId) === shiftIdParam) return;
		navigate(
			{
				pathname: `${routeDelaysManager}/${shiftId}`,
				search: window.location.search,
			},
			{ replace: true },
		);
	}, [shiftInfo, navMode, shiftIdParam, navigate]);

	// Initialize shift navigator from URL param or latest shift
	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0);
			})();
		}
	}, []);

	const handleStartDateChange = (date: Moment | null) => {
		if (isUsableDate(date) && date) setFilters({ start: date });
	};

	const handleEndDateChange = (date: Moment | null) => {
		if (isUsableDate(date) && date) setFilters({ end: date });
	};

	const handleExportToExcel = () => {
		if (!gridApiRef.current) return;

		gridApiRef.current.exportDataAsExcel({
			fileName: "Stops_Manager_Export.xlsx",
			columnKeys: [
				"Start",
				"End",
				"Duration",
				"DelayCodeName",
				"DelayCodePath",
				"DelayCategory",
				"Comments",
				"ProductName",
				"Crew",
				"Shift",
			],
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "Start" || colId === "End") {
					return params.value
						? dxtToLocalServerTime(params.value, "yyyy-MM-dd HH:mm:ss")
						: "";
				}
				if (colId === "Duration") {
					return formatDuration(params.value);
				}
				if (colId === "DelayCategory") {
					return params.value?.name || "";
				}
				return params.value;
			},
		});
	};

	return (
		<>
			<HelmetDexteel title="Stops Manager" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					px: 2,
					pt: 1,
					pb: 2,
					height: "calc(100vh - 64px)",
				}}
			>
				<Box>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Stops Manager
					</Typography>
				</Box>
				<Paper elevation={3}>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							gap: 1,
							px: 1.5,
							py: 0.75,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								userSelect: "none",
							}}
						>
							<Typography
								variant="body2"
								onClick={() => setFilters({ mode: "period" })}
								sx={{
									cursor: "pointer",
									fontWeight: 600,
									color:
										navMode === "period" ? "primary.main" : "text.secondary",
								}}
							>
								Period
							</Typography>
							<Switch
								checked={navMode === "shift"}
								onChange={() =>
									setFilters({ mode: navMode === "shift" ? "period" : "shift" })
								}
								size="small"
							/>
							<Typography
								variant="body2"
								onClick={() => setFilters({ mode: "shift" })}
								sx={{
									cursor: "pointer",
									fontWeight: 600,
									color:
										navMode === "shift" ? "primary.main" : "text.secondary",
								}}
							>
								Shift
							</Typography>
						</Box>
						{navMode === "shift" ? (
							// The navigator grows to fill the row; held to its content so the filter
							// and RESET buttons stay next to it, as they do next to the dates.
							<Box sx={{ flex: "0 0 auto" }}>
								<ShiftDayNavigatorControl preventInit withPaper={false} />
							</Box>
						) : (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Box sx={{ minWidth: 160 }}>
									<StartFilter
										value={filterStartDate}
										onChange={handleStartDateChange}
										maxDate={filterEndDate}
									/>
								</Box>
								<Box sx={{ minWidth: 160 }}>
									<EndFilter
										value={filterEndDate}
										onChange={handleEndDateChange}
										minDate={filterStartDate}
									/>
								</Box>
							</Box>
						)}
						<Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
							<Button
								variant="contained"
								size="medium"
								onClick={() => setShowFilters(!showFilters)}
								sx={{
									// Same height and dense margin as RESET and the date fields.
									mt: 1,
									mb: 0.5,
									minWidth: 40,
									px: 1,
									backgroundColor: showFilters ? "grey.500" : "primary.main",
									"&:hover": {
										backgroundColor: showFilters ? "grey.600" : "primary.dark",
									},
								}}
							>
								<FilterList />
							</Button>
						</Tooltip>
						<ResetFiltersButton
							onClick={() =>
								resetValues([
									"start",
									"end",
									"delayCategoryId",
									"delayCodeId",
									"crewId",
									"productTypeId",
									"productSizeId",
									"search",
								])
							}
							disabled={isDefault(
								"start",
								"end",
								"delayCategoryId",
								"delayCodeId",
								"crewId",
								"productTypeId",
								"productSizeId",
								"search",
							)}
						/>
						<Box sx={{ width: 200, ml: "auto" }}>
							<ExcelButton
								handleExportToExcel={handleExportToExcel}
								disabled={isLoading || displayRows.length === 0}
							/>
						</Box>
					</Box>
					{showFilters && (
						<Box
							sx={{
								display: "flex",
								flexWrap: "nowrap",
								alignItems: "center",
								gap: 1,
								px: 1.5,
								pb: 0.75,
							}}
						>
							<DelaysManagerFilters
								assetId={assetId}
								delayCategoryId={delayCategoryId}
								delayCodeId={delayCodeId}
								crew={crew}
								productTypeId={productTypeId}
								productSizeId={productSizeId}
								quickFilter={quickFilter}
								onDelayCategoryChange={(value) =>
									setFilters({ delayCategoryId: value })
								}
								onDelayCodeChange={(value) =>
									setFilters({ delayCodeId: value })
								}
								onCrewChange={(value) => setFilters({ crewId: value })}
								onProductTypeChange={(value) =>
									setFilters({ productTypeId: value, productSizeId: null })
								}
								onProductSizeChange={(value) =>
									setFilters({ productSizeId: value })
								}
								onQuickFilterChange={setQuickFilter}
							/>
						</Box>
					)}
				</Paper>
				{/* Summary: Count and Duration */}
				<Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							minHeight: 24,
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							Count:{" "}
							<span style={{ fontWeight: 400 }}>
								{displaySummary.count.toLocaleString()}
							</span>
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							Total Delay Time:{" "}
							<span style={{ fontWeight: 400 }}>
								{formatDuration(displaySummary.durationSeconds)}
							</span>
						</Typography>
					</Box>
				</Box>
				<Box sx={{ flex: 1, minHeight: 0 }}>
					<DelaysManagerTable
						delayId={delayId}
						action={action}
						isLoading={isLoading}
						rows={displayRows}
						refreshData={refetch}
						assetId={assetId!}
						updateUrl={updateUrl}
						quickFilter={quickFilter}
						onGridApiReady={(api) => {
							gridApiRef.current = api;
						}}
						onDialogOpenChange={setIsStopDialogOpen}
					/>
				</Box>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
